// Contexte d'authentification : résout la session au montage, expose refresh/logout.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { markSessionEstablished } from '@orgazone/ui-web';
// ⚠️ Sous-chemin `/client` OBLIGATOIRE : la racine `@orgazone/auth-web` est le plugin Fastify (argon2,
// fastify-plugin) — l'importer ici ferait entrer du code serveur dans le bundle navigateur.
import { resolveSession, type AuthMode } from '@orgazone/auth-web/client';
import { api, type User } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Mode d'auth du service (SSO configuré ? auth armée ?) — lu de /v1/auth/mode au boot. */
  mode: AuthMode;
  /**
   * Panne d'authentification : la session n'a pas pu être ÉTABLIE (service injoignable, 500…).
   * ⚠️ Distinct de `user === null`, qui signifie « pas connecté ». La garde AFFICHE ce message au lieu
   * de rediriger : envoyer vers un service en panne ne mène nulle part et fait retaper des identifiants
   * pour rien (§23.0, échec avalé).
   */
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null, loading: true, mode: { oidc: false, armed: true }, error: null,
  refresh: async () => {}, logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [mode, setMode] = useState<AuthMode>({ oidc: false, armed: true });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Résolution de session. La RÈGLE (fail-closed si le mode est illisible, 401 = simplement pas connecté,
  // toute autre panne = état DIT et non masqué) vit dans @orgazone/auth-web/client : identique
  // store/console/edi, testée là-bas. Ici on ne fait que la brancher sur l'état React.
  const refresh = useCallback(async () => {
    // ⚠️ finally OBLIGATOIRE : si resolveSession lève (bug/JSON invalide), `loading` resterait true
    // → « Chargement… » plein écran à jamais. L'exception devient une panne DITE, jamais un gel.
    try {
      const s = await resolveSession<User>({
        fetchMode: () => api.mode(),
        fetchUser: async () => (await api.me()).user,
      });
      setMode(s.mode);
      setUser(s.user);
      markSessionEstablished(); // distingue « session perdue » de « jamais connecté » (politique 401)
      setError(s.error);
    } catch (e) {
      setUser(null);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /**
   * Termine la session serveur puis vide l'utilisateur local (best-effort).
   *
   * ⚠️ NE PAS UTILISER POUR UNE VRAIE DÉCONNEXION. Le `setUser(null)` réveille la garde de route, qui
   * constate l'absence d'utilisateur et relance AUSSITÔT une connexion SSO — la session de l'IdP étant
   * encore vivante, elle réussit en silence et l'utilisateur se retrouve connecté. Une déconnexion doit
   * appeler `api.logout()` puis naviguer vers `/v1/auth/oidc/logout` (cf. `App.tsx`), qui termine aussi
   * la session de l'IdP. ⚠️ Le `catch` vide ci-dessous rend en outre tout échec invisible à l'appelant.
   *
   * Reste utile pour un abandon LOCAL de session (bris de glace, bascule de mode) où l'on veut
   * seulement oublier l'utilisateur côté navigateur.
   */
  const logout = async () => {
    try { await api.logout(); } catch { /* best-effort : on vide localement même si l'appel échoue */ }
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, mode, error, refresh, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
/** Vrai si l'utilisateur est super-admin (rôle staff le plus élevé). */
export const isSuperadmin = (u: User | null) => !!u?.roles.includes('superadmin');

/**
 * Droit EFFECTIF sur une action, tel que le serveur l'appliquera.
 * ⚠️ On lit les permissions SERVIES par `/auth/me` (jamais une matrice recopiée côté navigateur, qui
 * divergerait) : un bouton visible ici est un bouton que l'API acceptera. Le joker mono-niveau
 * (`console.*`) suit exactement la règle de `hasPermission` côté serveur.
 * Compte sans champ `permissions` (serveur plus ancien) : on retombe sur le seul rôle super-admin,
 * donc on RESTREINT — jamais l'inverse.
 */
export function can(u: User | null, permission: string): boolean {
  if (!u) return false;
  const perms = u.permissions;
  if (!perms) return isSuperadmin(u);
  if (perms.includes(permission) || perms.includes('*')) return true;
  const prefix = permission.split('.')[0];
  return prefix !== undefined && perms.includes(`${prefix}.*`);
}
